import { renderDonutForPdf, renderBarForPdf, renderGroupedBarForPdf, renderDualAxesForPdf, drawChartRow, drawChartSection } from "../pdfCharts";
import { formatWhole, formatMagnitudeAdaptive } from "../../../../utils/numberFormat";
import { BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { Waterform } from "../../../../api";
import {
  buildEffluentComparisonIndustrialData,
  buildEffluentIntensityIndustrialData,
  buildEffluentComparisonDomesticData,
  buildEffluentIntensityDomesticData,
  buildSharedYScale,
} from "./effluentChartData";
import {
  getEffluentTypeTotals,
  buildEffluentDonutData,
  buildEffluentBarChartData,
  buildEffluentWaterData,
  fetchEffluentWaterIndicators,
} from "./TableGraf";

const waterController = new Waterform();

// "Enero" -> "Ene", etc. — con los gráficos compartiendo página el nombre
// completo del mes no entra sin superponerse (mismo ajuste que en Energía/
// Combustibles/Agua).
const monthLabel3 = (v) => String(v).slice(0, 3);

function buildComparisonChildren(barData, lineData, yearColorMap, sharedYScale) {
  return [
    {
      data: barData,
      type: "interval",
      yField: "value",
      colorField: "year",
      group: true,
      style: { maxWidth: 40 },
      scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
    },
    {
      data: lineData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
    },
  ];
}

function buildMonthlyChildren(consumptionData, averageData, sharedYScale) {
  return [
    {
      data: consumptionData,
      type: "line",
      yField: "value",
      colorField: "year",
      connectNulls: false,
      style: { lineWidth: 4 },
      scale: { y: sharedYScale },
    },
    {
      data: averageData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
      connectNulls: false,
    },
  ];
}

// "Evolución industrial"/"Evolución doméstica" — barras de efluente mensual
// por año + línea punteada de promedio, misma config que
// EffluentComparisonChartIndustrial/Domestic.
async function renderComparisonForPdf(buildData, resultsByYear, t) {
  const { years, barData, lineData, yearColorMap } = buildData(resultsByYear, t);
  const sharedYScale = buildSharedYScale(barData, lineData);
  const children = buildComparisonChildren(barData, lineData, yearColorMap, sharedYScale);

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: {
      color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) },
      x: { paddingOuter: 0.1 },
    },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: false, grid: true, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

export async function renderEffluentComparisonIndustrialForPdf(resultsByYear, t) {
  return renderComparisonForPdf(buildEffluentComparisonIndustrialData, resultsByYear, t);
}

export async function renderEffluentComparisonDomesticForPdf(resultsByYear, t) {
  return renderComparisonForPdf(buildEffluentComparisonDomesticData, resultsByYear, t);
}

// "Intensidad industrial"/"Intensidad doméstica" — consumo mensual (línea
// sólida) + promedio (línea punteada), misma config que
// EffluentMonthlyChartIndustrial/Domestic.
async function renderIntensityForPdf(buildData, resultsByYear, divisorSource, t) {
  const { years, consumptionData, averageData, yearColorMap } = buildData(resultsByYear, divisorSource, t);
  const sharedYScale = buildSharedYScale(consumptionData, averageData);
  const children = buildMonthlyChildren(consumptionData, averageData, sharedYScale);

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: { color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) } },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: false, grid: true, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatMagnitudeAdaptive(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

export async function renderEffluentIntensityIndustrialForPdf(resultsByYear, resultsProductions, t) {
  return renderIntensityForPdf(buildEffluentIntensityIndustrialData, resultsByYear, resultsProductions, t);
}

export async function renderEffluentIntensityDomesticForPdf(resultsByYear, resultsWorkers, t) {
  return renderIntensityForPdf(buildEffluentIntensityDomesticData, resultsByYear, resultsWorkers, t);
}

// Los 3 gráficos de TableGraf.js (dona de tipos + barra anual + barra
// agrupada "vs Agua") como SVG vectorial — misma lógica de datos que ya usa
// TableGraf.js en pantalla, reutilizada tal cual. La barra "vs Agua"
// necesita su propio fetch a la API de Agua (fetchEffluentWaterIndicators),
// igual que hace TableGraf.js en su useEffect.
export async function renderEffluentChartsForPdf(totales, filters, { accessToken, site }) {
  const groupTotals = getEffluentTypeTotals(totales, [filters?.year]);
  const donutData = buildEffluentDonutData(groupTotals);

  const waterIndicators = await fetchEffluentWaterIndicators(waterController, accessToken, site, filters?.year);
  const barYearData = buildEffluentBarChartData(totales).map((d) => ({ year: d.year, value: d.total }));
  const groupedData = buildEffluentWaterData(totales, waterIndicators);

  const [donut, barAnual, barWater] = await Promise.all([
    renderDonutForPdf(donutData, DONUT_COLORS.effluent),
    // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino separador de
    // miles, sin decimales — mismo criterio que ya tienen las 4 secciones
    // DualAxes de más abajo.
    renderBarForPdf(barYearData, "M3", BAR_PALETTE, { yFormatter: formatWhole }),
    renderGroupedBarForPdf(groupedData, DONUT_COLORS.effluentWater, "M3", { yFormatter: formatWhole }),
  ]);

  return { donut, barAnual, barWater };
}

// Orquesta el dibujado de los 3 gráficos de TableGraf.js + los 4 DualAxes
// de Efluentes en el PDF: se pasa como `renderCustomCharts` a
// KPIExportIconButton. Mismo criterio de layout ya resuelto en
// Energía/Combustibles/Agua: fila de 3 tarjetas con altura fija, después
// las secciones DualAxes de a 2 por hoja, con techo de altura para no
// ocupar toda la hoja si queda solo.
export async function drawEffluentCustomCharts(doc, ctx, { totales, filters, accessToken, site, resultsByYear, resultsProductions, resultsWorkers, t }) {
  const { margin, pageWidth, pageHeight, imgWidth } = ctx;
  let yOffset = ctx.yOffset;

  const { donut, barAnual, barWater } = await renderEffluentChartsForPdf(totales, filters, { accessToken, site });
  const effluentCards = [
    { title: t("effluents_vs_water_consumption"), ...barWater },
    { title: t("annual_effluents"), ...barAnual },
    { title: t("types_effluents"), ...donut },
  ];
  const CHART_ROW_H = 210;
  const remaining = pageHeight - margin - yOffset;
  if (CHART_ROW_H > remaining) {
    doc.addPage();
    yOffset = margin;
  }
  await drawChartRow(doc, effluentCards, { x: margin, y: yOffset, availW: imgWidth, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") });
  yOffset += CHART_ROW_H + 10;
  donut.cleanup?.();
  barAnual.cleanup?.();
  barWater.cleanup?.();

  const GAP = 10;
  const PER_PAGE = 2;
  const MIN_EACH_H = 240;
  const MAX_EACH_H = 320;
  const sections = [
    { title: t("industrial_effluents_evolution"), render: () => renderEffluentComparisonIndustrialForPdf(resultsByYear, t) },
    { title: t("industrial_effluents_intensity"), render: () => renderEffluentIntensityIndustrialForPdf(resultsByYear, resultsProductions, t) },
    { title: t("domestic_effluents_evolution"), render: () => renderEffluentComparisonDomesticForPdf(resultsByYear, t) },
    { title: t("domestic_effluents_intensity"), render: () => renderEffluentIntensityDomesticForPdf(resultsByYear, resultsWorkers, t) },
  ];

  for (let i = 0; i < sections.length; i += PER_PAGE) {
    const group = sections.slice(i, i + PER_PAGE);
    let availForGroup = pageHeight - margin - yOffset - GAP * (group.length - 1);
    if (availForGroup < MIN_EACH_H * group.length) {
      doc.addPage();
      yOffset = margin;
      availForGroup = pageHeight - margin - yOffset - GAP * (group.length - 1);
    }
    const sectionH = Math.min(Math.floor(availForGroup / group.length), MAX_EACH_H);

    for (const section of group) {
      const result = await section.render();
      await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: sectionH, title: section.title, emptyMessage: t("no_data_available"), ...result });
      yOffset += sectionH + GAP;
      result.cleanup?.();
    }
  }

  return yOffset;
}
